import React, { useEffect, useState } from 'react'
import useSelectedFacility from '../../../hooks/useSelectedFacility'
import { Container, Typography, Card, CardHeader, CardContent, MenuItem } from '@material-ui/core'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import StyledTable from '../../../components/Common/StyledTable'
import { MTableCell } from '@material-table/core'
import { gray } from '../../../utils/colors'
import MoreMenu from '../../../components/Common/Menus/MoreMenu'
import { MainButton } from '../../../components'
import { AddOutlined } from '@material-ui/icons'
import TrainingLocationDialog from '../../../components/Dialogs/Training/TrainingLocationDialog'
import { TRAINING_LOCATION_TEMPLATE, DIALOG_EDIT_MODES } from '../../../utils'
import DangerConfirmationDialog from '../../../components/Common/Dialogs/DangerConfirmation'

const useStyles = makeStyles((theme) => ({
	root: {
		paddingTop: '1.5rem',
		paddingLeft: '1.5rem',
		paddingRight: '1.5rem',
		paddingBottom: '1.5rem'
	},
	card: { maxWidth: '945px' },
	cardContentContainer: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-between',
		gap: '1rem'
	},
	mainHeader: { fontWeight: 800, marginBottom: 16 },
	header: {
		fontSize: '.75rem',
		fontWeight: '800',
		letterSpacing: 0.75,
		marginTop: 24,
		marginBottom: 8,
		textTransform: 'uppercase'
	},
	cardHeader: {
		'& .MuiCardHeader-action': {
			margin: 0
		}
	}
}))

const TrainingLocations = () => {
	const classes = useStyles()
	const theme = useTheme()

	const [editMode, setEditMode] = useState(DIALOG_EDIT_MODES.New)
	const [editDialogOpen, setEditDialogOpen] = useState(false)
	const [currentEditedObject, setCurrentEditedObject] = useState(TRAINING_LOCATION_TEMPLATE)
	const [confirmRemoveAreaDialogOpen, setConfirmRemoveAreaDialogOpen] = useState(false)
	const [locationToRemove, setLocationToRemove] = useState(null)

	const {
		facility,
		getTrainingLocationsForFacility,
		loading,
		trainingLocations,
		updateTrainingLocationForFacility
	} = useSelectedFacility()

	useEffect(() => {
		const fetchLocations = async () => {
			await getTrainingLocationsForFacility()
		}
		if (facility) {
			fetchLocations()
		}
	}, [facility, getTrainingLocationsForFacility])

	const handleEditDialogOpen = ({ editMode = DIALOG_EDIT_MODES.New }) => {
		setEditMode(editMode)
		setEditDialogOpen(true)
	}

	const handleEditDialogClose = () => {
		setEditDialogOpen(false)
	}

	const handleEditLocationButtonClick = (existing) => {
		setCurrentEditedObject({ ...existing })
		handleEditDialogOpen({ editMode: DIALOG_EDIT_MODES.Edit })
	}

	const handleCloseConfirmRemoveAreaDialog = (location) => {
		setLocationToRemove(null)
		setConfirmRemoveAreaDialogOpen(false)
	}

	const handleOpenConfirmRemoveAreaDialog = (location) => {
		setLocationToRemove(location)
		setConfirmRemoveAreaDialogOpen(true)
	}

	const handleRemoveArea = async (location) => {
		await updateTrainingLocationForFacility({
			location_id: locationToRemove?._id,
			updates: { deactivated: true },
			facility_id: facility._id
		})
		handleCloseConfirmRemoveAreaDialog()
	}

	const handleNewLocationButtonClick = () => {
		setCurrentEditedObject({ ...TRAINING_LOCATION_TEMPLATE })
		handleEditDialogOpen({ editMode: DIALOG_EDIT_MODES.New })
	}

	const columns = [
		{
			title: 'Name',
			field: 'name',
			width: '30%'
		},
		{
			title: 'Abbreviation',
			field: 'abbreviation',
			width: '10%'
		},
		{
			title: 'Address',
			width: '40%',
			sorting: false,
			render: (data) =>
				data.address ? (
					<span>{`${data.address?.address_line_1}, ${data.address?.city}, ${data.address?.state} ${data.address?.zip_code}`}</span>
				) : (
					'no address'
				)
		},
		{
			title: '',
			width: 50,
			sorting: false,
			render: (d) => {
				const menuOptions = []
				menuOptions.push(
					<MenuItem key="edit-location" onClick={() => handleEditLocationButtonClick(d)}>
						Edit Location
					</MenuItem>
				)
				menuOptions.push(
					<MenuItem key="remove-location" onClick={() => handleOpenConfirmRemoveAreaDialog(d)}>
						Remove Location
					</MenuItem>
				)
				return <MoreMenu options={menuOptions} description={d.name} useHoverState />
			}
		}
	]

	return (
		<>
			<TrainingLocationDialog
				open={editDialogOpen}
				onClose={handleEditDialogClose}
				onSaved={handleEditDialogClose}
				onCancelled={handleEditDialogClose}
				title={
					editMode === DIALOG_EDIT_MODES.New
						? 'New Area'
						: editMode === DIALOG_EDIT_MODES.Edit
						? 'Edit Area'
						: 'View Area'
				}
				location={currentEditedObject}
				mode={editMode}
			/>
			<DangerConfirmationDialog
				title="Remove Area"
				open={confirmRemoveAreaDialogOpen}
				description={`Are you sure you want to remove ${
					locationToRemove?.name || 'this area'
				}? You will lose all editing capabilities and the ability to set up courses using this area.`}
				dangerButtonText="Remove"
				onConfirmDangerousAction={handleRemoveArea}
				onCancel={handleCloseConfirmRemoveAreaDialog}
			/>
			<div>
				<Container className={classes.root} maxWidth="auto">
					<Typography variant="h5" className={classes.mainHeader}>
						Locations
					</Typography>
					<Card className={classes.card}>
						<CardHeader
							className={classes.cardHeader}
							title="Areas"
							action={
								<MainButton
									label="New Area"
									onClick={handleNewLocationButtonClick}
									startIcon={<AddOutlined style={{ fill: theme.palette.secondary.main }} />}
								/>
							}
						/>
						<CardContent>
							<div className={classes.cardContentContainer}>
								<StyledTable
									isLoading={loading}
									columns={columns}
									data={trainingLocations}
									onRowClick={() => null}
									pageSize={10}
									paging={false}
									style={{
										boxShadow: 'none',
										minHeight: '500px'
									}}
									headerStyle={{
										backgroundColor: gray[100],
										borderTop: 'none'
									}}
									hideBorderBottom
									overrideOptions={{ pageSizeOptions: [10] }}
									components={{
										Cell: (props) => {
											return (
												<MTableCell
													style={{
														borderBottom: 'none'
													}}
													{...props}
												/>
											)
										}
									}}
								/>
							</div>
						</CardContent>
					</Card>
				</Container>
			</div>
		</>
	)
}

export default TrainingLocations
